import React from "react";
import { useNavigate } from "react-router-dom";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import e3dModel from "../../model/e3dModel";
import ImgUtil from "../../utils/ImgUtil";
import TimeUtil from "../../utils/TimeUtil";
import EntityStats from "../../model/EntityStats";
import ChemiNovaEntityType from "../../model/enums/ChemiNovaEntityType";
import { Tooltip } from "react-tooltip";
import ButtonAction from "../common/button/ButtonAction";
import ButtonType from "../common/button/ButtonType";
import Button from "../common/button/Button";
import ConditionReport from "../../model/ConditionReport";
import DocumentState from "../../model/enums/DocumentState";

interface IE3dListElementProps {
  data: e3dModel;
  canEdit: boolean;
  index: number;
}

function E3dListElement({ data, canEdit, index }: IE3dListElementProps) {
  // reference to the navigator
  const navigate = useNavigate();

  const bgCSS = index > 0 ? "bg-amber-50" : "bg-white";

  const handleDownloadReport = async (reportId: number) => {
    window.location.href =
      SETTINGS.API_ROOT + `/conditionreports/${reportId}/download`;
  };

  return (
    <div
      className={"condition-assessment-element bg-center cursor-pointer"}
      style={{
        backgroundImage: `url(${ImgUtil.display_static_image(
          "dashed_background_vertical.png",
        )})`,
        backgroundPositionX: "center",
        backgroundRepeat: "repeat-y",
      }}
      onClick={() => {
        navigate(
          SETTINGS.get_path({
            section: AppSections.E3DS,
            id: data.id!,
            subsection: AppSubSections.VIEW,
          }),
        );
      }}
    >
      <div className={"assessment-date shadow-md mb-16 rounded-md " + bgCSS}>
        <h2 style={{ marginBottom: "0" }}>
          {TimeUtil.display_month_year(data.reference_date)}
        </h2>
      </div>
      <div className="p-0 text-left bg-transparent">
        {false && (
          <div
            className="details-grid-icon"
            style={{
              backgroundImage: `url(${ImgUtil.display_icon(data.icon_path)})`,
            }}
          ></div>
        )}
        <div
          className={
            "inline-block py-2 px-5 rounded-tl-md rounded-tr-md " + bgCSS
          }
        >
          <h3>Details</h3>
        </div>
        <div
          className={"details-grid-content rounded-bl-md rounded-e-md " + bgCSS}
        >
          <div className="details-grid-description">
            <div>
              <strong>{data.name}</strong>
            </div>
            <p>{data.description}</p>
          </div>
        </div>
      </div>
      <div className="p-0 text-left mt-6">
        <div
          className={
            "inline-block py-2 px-5 rounded-tl-md rounded-tr-md " + bgCSS
          }
        >
          <h3>
            e3D
            <span className="tooltip-hint">
              <a data-tooltip-id="e3d-tooltip" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="e3d-tooltip">
                Refers to a 3D representation of a cultural heritage asset
                <br />
                created by combining multiple scans performed with different
                <br />
                sensors, enriched with various data that represent the state of
                <br />
                the asset at a specific moment in time.
              </Tooltip>
            </span>
          </h3>
        </div>
        <div
          className={"details-grid-content rounded-bl-md rounded-e-md " + bgCSS}
        >
          <div className="details-grid-statistics grid grid-cols-2">
            <ButtonInfo>
              {AppIcons.USER}
              <span>{data.username}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.E3D}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType._3DINSTANCE,
                )}
              </span>
              3D scans
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.ANNOTATIONS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ANNOTATION,
                )}
              </span>
              annotations
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.ALTERATION_FORMS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ALTERATION_EVENT,
                )}
              </span>
              alteration events
            </ButtonInfo>
          </div>
        </div>
      </div>
      <div className="p-0 text-left mt-6">
        <div
          className={
            "inline-block py-2 px-5 rounded-tl-md rounded-tr-md " + bgCSS
          }
        >
          <h3>Condition report</h3>
        </div>
        <div
          className={"details-grid-content rounded-bl-md rounded-e-md " + bgCSS}
        >
          {data.condition_report && (
            <div className="details-grid-description">
              Current status: {data.condition_report.status}
              <br />
              Progress: {ConditionReport.GetProgress(data.condition_report)}%
              complete
              <br />
              <br />
              <div className="flex w-full flex-row justify-between">
                {data.condition_report.status !== DocumentState.public &&
                  canEdit && (
                    <Button
                      purpose={ButtonAction.EDIT}
                      type={ButtonType.ICON_TEXT}
                      label="Edit"
                      className="ml-0"
                      onClick={() =>
                        navigate(
                          SETTINGS.get_path({
                            section: AppSections.E3DS,
                            id: data.id,
                            additionalSections: [
                              AppSections.CONDITION_REPORTS,
                              data.condition_report?.id?.toString() ?? "",
                            ],
                            subsection: AppSubSections.EDIT,
                          }),
                        )
                      }
                    />
                  )}
                <Button
                  purpose={ButtonAction.DOWNLOAD}
                  type={ButtonType.ICON_TEXT}
                  label="Download"
                  className="ml-0"
                  onClick={() =>
                    handleDownloadReport(data.condition_report?.id!)
                  }
                />
              </div>
            </div>
          )}
          {!data.condition_report && (
            <div className="details-grid-description">
              Condition report has not been created yet.
              <br />
              {canEdit && (
                <Button
                  label="Create Condition Report"
                  purpose={ButtonAction.CREATE}
                  type={ButtonType.ICON_TEXT}
                  className="mt-4 ml-0"
                  onClick={() =>
                    navigate(
                      SETTINGS.get_path({
                        section: AppSections.E3DS,
                        id: data.id,
                        // additionalSections: [AppSections.CONDITION_REPORTS],
                        additionalSections: [
                          `${data.id_chelement}/${AppSections.CONDITION_REPORTS}`,
                        ], // It needs the id of the parent CHElement also => as specified in the Routes (E3dRoutes)
                        subsection: AppSubSections.CREATE,
                      }),
                    )
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default E3dListElement;
